import type { QueryRollupLevelOption } from "./types";

const LEVEL_SEPARATOR = " > ";
/** Full path shown for up to two segments; longer stacks collapse to
 *  `... > <deepest>`. The full path is always available via the HelpIndicator
 *  tooltip. */
export const MAX_FULL_LEVEL_SEGMENTS = 2;

const findOptionByValue = (
  value: string,
  options: ReadonlyArray<QueryRollupLevelOption>,
): QueryRollupLevelOption | undefined => options.find((option) => option.value === value);

const findOptionByLabel = (
  label: string,
  options: ReadonlyArray<QueryRollupLevelOption>,
): QueryRollupLevelOption | undefined =>
  options.find((option) => option.label.toLowerCase() === label.toLowerCase());

export const formatLevelLabel = (
  value: string,
  options: ReadonlyArray<QueryRollupLevelOption>,
): string => {
  const byValue = findOptionByValue(value, options);
  if (byValue) return byValue.label;
  const byLabel = findOptionByLabel(value, options);
  if (byLabel) return byLabel.label;
  return value;
};

export const normalizeLevelStack = (
  levels: readonly string[],
  options: ReadonlyArray<QueryRollupLevelOption>,
): string[] =>
  levels.map((level) => {
    const byValue = findOptionByValue(level, options);
    if (byValue) return byValue.value;
    const byLabel = findOptionByLabel(level, options);
    if (byLabel) return byLabel.value;
    return level;
  });

/** First catalogue entry used to seed a new field's rollup level. Returns ""
 * when the catalogue is empty so callers can detect "no default" rather than
 * receiving an invented level value. */
export const getDefaultRollupLevelValue = (
  options: ReadonlyArray<QueryRollupLevelOption>,
): string => options[0]?.value ?? "";

const formatLevelStackLabels = (
  levels: readonly string[],
  options: ReadonlyArray<QueryRollupLevelOption>,
): string[] => levels.map((level) => formatLevelLabel(level, options));

export const formatLevelStackColumnDisplay = (labels: readonly string[]): string => {
  const stack = labels.filter((label) => label.length > 0);
  if (stack.length === 0) return "--";
  if (stack.length <= MAX_FULL_LEVEL_SEGMENTS) {
    return stack.join(LEVEL_SEPARATOR);
  }
  return `...${LEVEL_SEPARATOR}${stack[stack.length - 1]}`;
};

export const deriveLevelColumnDisplay = (
  levels: readonly string[] | null | undefined,
  options: ReadonlyArray<QueryRollupLevelOption>,
): string => {
  const stack = levels?.filter((level) => level.length > 0) ?? [];
  if (stack.length === 0) return "--";
  return formatLevelStackColumnDisplay(formatLevelStackLabels(stack, options));
};

/** Full untruncated level path for tooltip content, e.g.
 *  "Entity > Account > Position > Deal". Returns "--" when empty. */
export const deriveLevelFullDisplay = (
  levels: readonly string[] | null | undefined,
  options: ReadonlyArray<QueryRollupLevelOption>,
): string => {
  const stack = levels?.filter((level) => level.length > 0) ?? [];
  if (stack.length === 0) return "--";
  return formatLevelStackLabels(stack, options).join(LEVEL_SEPARATOR);
};
